<script setup>
// --- Imports ---
import {ref, computed, watch, onMounted, onBeforeUnmount} from 'vue';
import axios from "axios";
import {useMiniConfigurateurStore} from "../stores/miniConfigurateur";
import {stagePrice} from '../utils/pricing';
import vehiclePlaceholder from '../../img/configurator-placeholder-ferrari-296.webp';
import circuitPlaceholder from '../../img/configurator-placeholder-driving-center.webp';
// Assurez-vous que la directive v-choices est correctement importée et enregistrée globalement ou localement
// import { vChoices } from './directives/vChoices'; // Exemple d'import local

const storeMini = useMiniConfigurateurStore();

// Racine du composant, utilisée pour détecter un clic en dehors et refermer
// les listes voitures/circuits ouvertes.
const rootEl = ref(null);

const onDocumentClick = (event) => {
    if (!rootEl.value) return;
    if (!showCarsList.value && !showCircuitsList.value) return;
    // composedPath() reflète le chemin au moment du dispatch, avant toute
    // mutation DOM déclenchée par Vue (v-if retirant la cible du clic).
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (path.includes(rootEl.value)) return;
    showCarsList.value = false;
    showCircuitsList.value = false;
};

onMounted(() => {
    document.addEventListener('click', onDocumentClick);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', onDocumentClick);
});

const props = defineProps({
    data: {
        type: Object,
        default: () => ({
            carImageUrl: '', carLabel: 'Choisir une voiture', carPlaceholder: 'Sélectionnez...',
            circuitImageUrl: '', circuitLabel: 'Choisir un circuit', circuitPlaceholder: 'Sélectionnez...',
            roundLabel: 'Tours', priceLabel: 'À partir de', reserveLabel: 'Réserver',
            orLabel: 'ou', phoneNumber: ''
        })
    },
});

// --- Refs pour les données et l'état ---
const data = ref(props.data);
const cars = ref([]); // Contiendra les voitures chargées
const circuits = ref([]); // Contiendra les circuits chargés dynamiquement
const circuitsList = ref([]); // Contiendra les circuits chargés dynamiquement
const stages = ref([]);       // Stages globaux (pour expérience)
const autreStages = ref([]);  // Autres stages globaux (pour expérience)
const voiture_id = ref(null);
const circuit_id = ref(null);
const nb_tours = ref(0);
const isLoadingCars = ref(false);
const isLoadingCircuits = ref(false);
const showCarsList = ref(false);
const showCircuitsList = ref(false);

if (storeMini.voiture_id) {
    voiture_id.value = storeMini.voiture_id;
    fetchCircuits(voiture_id.value);
    // Charger aussi les stages/autreStages pour déduire l'expérience (en fonction de voiture_id)
    axios.get('/api/configurator/global', {params: {voiture_id: voiture_id.value}})
        .then(response => {
            const datas = response?.data?.datas || {};
            stages.value = datas.stages || [];
            autreStages.value = datas.autreStages || [];
        })
        .catch(() => {
            stages.value = [];
            autreStages.value = [];
        });
} else {
    isLoadingCars.value = true;
    axios.get('/api/configurator/global').then(response => {
        const datas = response?.data?.datas || {};
        cars.value = datas.voitures || [];
        circuitsList.value = datas.circuits || [];
        stages.value = datas.stages || [];
        autreStages.value = datas.autreStages || [];
        if (cars.value.length) {
            data.value.carImageUrl = cars.value[0].image;
        }
        if (circuitsList.value.length) {
            data.value.circuitImageUrl = circuitsList.value[0].image_circuit;
        }
    }).finally(() => {
        isLoadingCars.value = false;
    })
}


// --- Fonction pour charger les circuits d'une voiture ---
async function fetchCircuits(carId) {
    if (storeMini.circuits.length) {
        circuits.value = storeMini.circuits;
        return;
    }
    if (!carId) {
        circuits.value = [];
        circuit_id.value = null; // Réinitialiser la sélection du circuit
        return;
    }
    isLoadingCircuits.value = true;
    circuits.value = []; // Vider les circuits précédents
    circuit_id.value = null; // Réinitialiser la sélection
    if (circuitsList.value.length && false) {
        circuits.value = circuitsList.value.filter(circuit => circuit.voiture_ids.find(id => id == carId));
        isLoadingCircuits.value = false;
        return;
    }
    try {
        const response = await axios.get(`/api/configurator/circuit-lite`, {
            params: {
                voiture_id: carId
            } // Passer voiture_id comme paramètre
        });
        // Assurez-vous que la structure est correcte
        circuits.value = Array.isArray(response.data.datas.circuits) ? response.data.datas.circuits : [];
    } catch (error) {
        console.error("Erreur lors du chargement des circuits:", error);
        circuits.value = [];
    } finally {
        isLoadingCircuits.value = false;
    }
}

// --- Fonctions pour afficher/masquer les listes ---
function toggleCarsSelection() {
    showCarsList.value = !showCarsList.value;
}

function toggleCircuitsSelection() {
    if (!voiture_id.value) return;
    showCircuitsList.value = !showCircuitsList.value;
}

// --- Gestion des changements de sélection ---
function onCarChange() {
    circuit_id.value = null; // Réinitialiser le circuit
    showCircuitsList.value = false; // Masquer la liste des circuits
    // Mettre à jour nb_tours basé sur la voiture sélectionnée
    const car = selectedCar.value;
    nb_tours.value = (car && car.price) ? Number(car.price.nb_tour_min ?? 0) : 0;
    if (nb_tours.value < 0) nb_tours.value = 0;

    // Charger les circuits correspondants
    fetchCircuits(voiture_id.value);

    // Masquer la liste des voitures après sélection
    showCarsList.value = false;
}

function onCircuitChange() {
    // Mettre à jour nb_tours basé sur le circuit sélectionné
    const circuit = selectedCircuit.value;
    if (circuit) {
        nb_tours.value = Number(circuit.stage_circuit_nb_tour_min ?? 0);
    } else {
        // Fallback si le circuit n'est pas trouvé (ou déselectionné)
        const car = selectedCar.value;
        nb_tours.value = (car && car.price) ? Number(car.price.nb_tour_min ?? 0) : 0;
    }
    if (nb_tours.value < 0) nb_tours.value = 0;

    // Masquer la liste des circuits après sélection
    showCircuitsList.value = false;
}

// --- Propriétés calculées (Computed Properties) ---
const selectedCar = computed(() => {
    if (!voiture_id.value || !Array.isArray(cars.value)) return null;
    return cars.value.find(car => car.id == voiture_id.value) || null;
});

// Les circuits filtrés sont simplement les circuits chargés pour la voiture sélectionnée
const filteredCircuits = computed(() => {
    return Array.isArray(circuits.value) ? circuits.value : [];
});

// Déterminer les capacités d'expérience pour la voiture sélectionnée
const hasBapteme = computed(() =>
    filteredCircuits.value.some(c => c && c.price && (c.price.is_bapteme === true || c.price.is_bapteme == 1))
);

const hasPilotage = computed(() =>
    filteredCircuits.value.some(c => c && (
        (c.price && (c.price.is_bapteme === false || c.price.is_bapteme == 0))
        || (c.price && (c.price.is_bapteme === null || typeof c.price.is_bapteme === 'undefined'))
    ))
);

// Options passées à la directive v-open-configurateur
const openOptions = computed(() => {
    const opts = {
        voiture_id: voiture_id.value,
        circuit_id: circuit_id.value,
        nb_tours: Number(nb_tours.value),
    };

    // Déduire l'expérience depuis datas.stages + datas.autreStages en fonction de la sélection actuelle
    const union = []
        .concat(Array.isArray(stages.value) ? stages.value : [])
        .concat(Array.isArray(autreStages.value) ? autreStages.value : []);

    const vid = Number(voiture_id.value);
    const cid = Number(circuit_id.value);

    const matches = union.filter(s => {
        const f = s?.form || {};
        return Number(f.voiture_id) === vid && Number(f.circuit_id) === cid;
    });

    const hasBapFromStages = matches.some(s => (s.is_bapteme === true || s.is_bapteme == 1));
    const hasPilFromStages = matches.some(s => (s.is_bapteme === false || s.is_bapteme == 0));

    // Fallback sur les flags calculés via circuits si aucune correspondance trouvée côté stages
    const bothFromStagesKnown = hasBapFromStages || hasPilFromStages;
    const hasBap = bothFromStagesKnown ? hasBapFromStages : hasBapteme.value;
    const hasPil = bothFromStagesKnown ? hasPilFromStages : hasPilotage.value;

    // Règle identique à card-voiture.blade.php
    const both = hasBap && hasPil;
    const explicit = typeof data.value?.isBapteme !== 'undefined' ? !!data.value.isBapteme : null;

    let exp = null;
    if ((explicit === null || explicit === false) && both) {
        // Ne pas passer l'expérience -> laisser la directive afficher le choix
        exp = null;
    } else if (explicit !== null) {
        // Priorité au choix explicite (propagée par le contexte d'affichage)
        exp = explicit ? 'bapteme' : 'pilotage';
    } else if (!both) {
        // Un seul type disponible -> forcer l'expérience
        exp = hasBap ? 'bapteme' : (hasPil ? 'pilotage' : null);
    }

    if (exp) {
        opts.experience = exp;
    }
    //console.log('Open options:', opts);

    return opts;
});

const selectedCircuit = computed(() => {
    if (circuit_id.value === null || !filteredCircuits.value.length) return null;
    return filteredCircuits.value.find(circuit => circuit.id == circuit_id.value) || null;
});

const currentRoundLimits = computed(() => {
    const circuit = selectedCircuit.value;
    const car = selectedCar.value;

    if (circuit) {
        // Utiliser les limites du circuit sélectionné
        return {
            min: Number(circuit.stage_circuit_nb_tour_min ?? 0),
            max: Number(circuit.stage_circuit_nb_tour_max ?? 0),
        };
    } else if (car && car.price) {
        // Si aucun circuit sélectionné, utiliser les limites du "price" de la voiture
        return {
            min: Number(car.price.nb_tour_min ?? 0),
            max: Number(car.price.nb_tour_max ?? 0),
        };
    }
    // Valeurs par défaut
    return {min: 0, max: 0};
});

// --- Watchers pour contraindre nb_tours ---
watch(nb_tours, (newValue, oldValue) => {
    const limits = currentRoundLimits.value;
    const numericValue = Number(newValue);
    const minLimit = Number(limits.min);
    const maxLimit = Number(limits.max);

    if (isNaN(numericValue)) {
        if (nb_tours.value !== minLimit) nb_tours.value = minLimit;
    } else if (numericValue < minLimit) {
        if (nb_tours.value !== minLimit) nb_tours.value = minLimit;
    } else if (maxLimit > 0 && numericValue > maxLimit) {
        if (nb_tours.value !== maxLimit) nb_tours.value = maxLimit;
    }
});

watch(currentRoundLimits, (newLimits, oldLimits) => {
    const currentTours = Number(nb_tours.value);
    const minLimit = Number(newLimits.min);
    const maxLimit = Number(newLimits.max);

    if (isNaN(currentTours) || currentTours < minLimit) {
        nb_tours.value = minLimit;
    } else if (maxLimit > 0 && currentTours > maxLimit) {
        nb_tours.value = maxLimit;
    }
}, {immediate: true});

// --- Computed pour l'état des boutons +/- ---
const canDecrease = computed(() => Number(nb_tours.value) > Number(currentRoundLimits.value.min));
const canIncrease = computed(() => {
    const maxLimit = Number(currentRoundLimits.value.max);
    return maxLimit === 0 || Number(nb_tours.value) < maxLimit;
});

// --- Méthodes pour les boutons +/- ---
function decreaseTourCount() {
    if (canDecrease.value) {
        const currentVal = Number(nb_tours.value);
        if (!isNaN(currentVal)) {
            nb_tours.value = currentVal - 1;
        }
    }
}

function increaseTourCount() {
    if (canIncrease.value) {
        const currentVal = Number(nb_tours.value);
        if (!isNaN(currentVal)) {
            nb_tours.value = currentVal + 1;
        }
    }
}

// --- Calcul et formatage du prix final ---
const finalPrice = computed(() => {
    const circuit = selectedCircuit.value;

    // Le prix est basé UNIQUEMENT sur le circuit sélectionné
    if (!circuit) {
        return 0; // Pas de prix si pas de circuit
    }
    const tours = Number(nb_tours.value);
    if (isNaN(tours) || tours < Number(circuit.price.nb_tour_min)) {
        return 0;
    }

    const price = stagePrice(circuit.price, tours);

    return price;
});

const formattedFinalPrice = computed(() => {
    const price = Number(finalPrice.value);
    if (isNaN(price)) return ' ';
    return price.toLocaleString('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    });
});
</script>

<template>
    <div class="form search-reserv" ref="rootEl">
        <div class="row align-items-center">

            <!-- Choisir une voiture -->
            <div class="col-md-5 col-lg-4">
                <div class="row d-flex align-items-center">
                    <div class="col-md-4">
                        <figure class="figure">
                            <img :src="selectedCar ? selectedCar.image : (data.carImageUrl || vehiclePlaceholder)"
                                 class="img-fluid"
                                 alt="Voiture">
                        </figure>
                    </div>
                    <div class="col-md-8">
                        <label class="form-label fw-bold">{{ data.carLabel }}</label>

                        <!-- Bouton de sélection ou voiture sélectionnée -->
                        <div v-if="!showCarsList" class="selection-button" @click="toggleCarsSelection"
                             style="cursor: pointer;">
                            <div class="form-control d-flex justify-content-between align-items-center">
                                <span v-if="selectedCar">{{ selectedCar.title_complete }}</span>
                                <span v-else-if="isLoadingCars">Chargement...</span>
                                <span v-else class="text-muted">{{ data.carPlaceholder }}</span>
                                <i class="bi bi-chevron-down"></i>
                            </div>
                        </div>

                        <!-- Liste des voitures -->
                        <div v-else>
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <span class="fw-bold">{{ data.carLabel }}</span>
                                <button class="btn btn-sm btn-outline-secondary" @click="showCarsList = false">
                                    <i class="bi bi-x"></i>
                                </button>
                            </div>

                            <div v-if="isLoadingCars" class="text-center py-3">
                                <span>Chargement...</span>
                            </div>
                            <div v-else class="card-step cars-selection">
                                <div class="row">
                                    <div class="col-4 col-lg-3 col-xl-2 p-1" v-for="car in cars" :key="car.id">
                                        <div class="card card-config card-voiture-config"
                                             :class="{ 'card-selected': voiture_id === car.id }"
                                             style="cursor: pointer;">
                                            <div class="form-check form-switch">
                                                <input @change="onCarChange"
                                                       v-model="voiture_id"
                                                       :value="car.id"
                                                       class="form-check-input"
                                                       type="radio"
                                                       name="car-product-search"
                                                       :id="'car-product-search-' + car.id">
                                            </div>

                                            <div @click="voiture_id = car.id; onCarChange()"
                                                 class="card-clickable-area">
                                                <img :src="car.image" alt="" class="card-config-bg">

                                                <div class="card-body">
                                                    <div class="card-text">
                                                        <span class="fw-bold">{{ car.title_complete }}</span>
                                                    </div>
                                                    <div class="card-price">
                                                        <div v-if="car.price && car.price.promo"
                                                             class="badge bg-danger text-white text-center fw-bolder">
                                                            -{{ car.price.promo }}
                                                        </div>
                                                        <span class="small fw-normal">{{ $t('À partir de ') }}</span>
                                                        <div class="price d-flex flex-row align-items-baseline">
                                                            <span
                                                                v-if="car.price && car.price.old_montant_ttc && car.price.old_montant_ttc !== car.price.montant_ttc"
                                                                class="old-price fw-normal text-decoration-line-through small fs-xxl-6 me-2">{{
                                                                    car.price.old_montant_ttc
                                                                }}€</span>
                                                            <span
                                                                class="text-info price fw-bolder small fs-xl-6 fs-xxl-5 p-lg-0 p-xl-0">{{
                                                                    car.price ? car.price.montant_ttc : ''
                                                                }}€</span>
                                                            <span class="nb fw-normal small">/{{
                                                                    car.price ? car.price.nb_tour_min : 0
                                                                }} tours</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Choisir un circuit -->
            <div class="col-md-5 col-lg-4">
                <div class="row d-flex align-items-center">
                    <div class="col-md-4">
                        <figure class="figure">
                            <img :src="selectedCircuit ? selectedCircuit.image_circuit : (data.circuitImageUrl || circuitPlaceholder)"
                                 class="img-fluid"
                                 alt="Circuit">
                        </figure>
                    </div>
                    <div class="col-md-8">
                        <label class="form-label fw-bold">{{ data.circuitLabel }}</label>

                        <!-- Bouton de sélection ou circuit sélectionné -->
                        <div v-if="!showCircuitsList" class="selection-button" @click="toggleCircuitsSelection"
                             style="cursor: pointer;">
                            <div class="form-control d-flex justify-content-between align-items-center">
                                <span v-if="!voiture_id" class="text-muted">Sélectionnez une voiture</span>
                                <span v-else-if="selectedCircuit">{{ selectedCircuit.title_complete }}</span>
                                <span v-else-if="isLoadingCircuits">Chargement...</span>
                                <span v-else-if="filteredCircuits.length === 0" class="text-muted">Aucun circuit disponible</span>
                                <span v-else class="text-muted">{{ data.circuitPlaceholder }}</span>
                                <i class="bi bi-chevron-down" v-if="voiture_id"></i>
                            </div>
                        </div>

                        <!-- Liste des circuits -->
                        <div v-else-if="voiture_id">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <span class="fw-bold">{{ data.circuitLabel }}</span>
                                <button class="btn btn-sm btn-outline-secondary" @click="showCircuitsList = false">
                                    <i class="bi bi-x"></i>
                                </button>
                            </div>

                            <div v-if="isLoadingCircuits" class="text-center py-3">
                                <span>Chargement...</span>
                            </div>
                            <div v-else-if="filteredCircuits.length === 0" class="text-center py-3">
                                <span class="text-muted">Aucun circuit disponible</span>
                            </div>
                            <div v-else class="card-step circuits-selection">
                                <div class="row">
                                    <div class="col-4 col-lg-3 col-xl-2 p-1" v-for="circuit in filteredCircuits"
                                         :key="circuit.id">
                                        <div class="card card-config card-circuit-config"
                                             :class="{ 'card-selected': circuit_id === circuit.id }"
                                             style="cursor: pointer;">
                                            <div class="form-check form-switch">
                                                <input @change="onCircuitChange"
                                                       v-model="circuit_id"
                                                       :value="circuit.id"
                                                       class="form-check-input"
                                                       type="radio"
                                                       name="circuit-product-search"
                                                       :id="'circuit-product-search-' + circuit.id">
                                            </div>

                                            <div @click="circuit_id = circuit.id; onCircuitChange()"
                                                 class="card-clickable-area">
                                                <img :src="circuit.image_circuit" alt="" class="card-config-bg">

                                                <div class="card-body">
                                                    <div class="card-text">
                                                        <span class="fw-bold">{{ circuit.title_complete }}</span>
                                                    </div>
                                                    <div class="card-price">
                                                        <div v-if="circuit.price && circuit.price.promo"
                                                             class="badge bg-danger text-white text-center fw-bolder">
                                                            -{{ circuit.price.promo }}
                                                        </div>
                                                        <span class="small fw-normal">{{ $t('À partir de ') }}</span>
                                                        <div class="price d-flex flex-row align-items-baseline">
                                                        <span
                                                            v-if="circuit.price && circuit.price.old_montant_ttc && circuit.price.old_montant_ttc !== circuit.price.montant_ttc"
                                                            class="old-price fw-normal text-decoration-line-through small fs-xxl-6 me-2">{{
                                                                circuit.price.old_montant_ttc
                                                            }}€</span>
                                                            <span
                                                                class="text-info price fw-bolder small fs-xl-6 fs-xxl-5 p-lg-0 p-xl-0">{{
                                                                    circuit.price ? circuit.price.montant_ttc : ''
                                                                }}€</span>
                                                            <span class="nb fw-normal small">/{{
                                                                    circuit.stage_circuit_nb_tour_min
                                                                }} tours</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Nombre de tours -->
            <div class="col-md-2 col-lg-1">
                <label class="form-label fw-bold">{{ data.roundLabel }}</label>
                <div class="d-flex justify-content-center align-items-center">
                    <button id="decrease" class="btn btn-icon fs-5" type="button" :disabled="!canDecrease"
                            @click="decreaseTourCount">
                        <i class="bi bi-dash-circle-fill"></i>
                    </button>
                    <input id="tourCount" class="form-control-plaintext fw-bold text-center mx-2" type="text" readonly
                           :value="nb_tours">
                    <button id="increase" class="btn btn-icon fs-5" type="button" :disabled="!canIncrease"
                            @click="increaseTourCount">
                        <i class="bi bi-plus-circle-fill"></i>
                    </button>
                </div>
            </div>

            <!-- Bloc prix, bouton Réserver, numéro de téléphone -->
            <div class="col-md-12 col-lg-3">
                <div class="row d-flex bg-dark border border-1 border-white rounded py-4 px-3">
                    <div class="col-5 p-0" v-if="finalPrice > 0">
                        <p class="fs-6 fw-normal text-gray-50 mb-1 small">{{ data.priceLabel }}</p>
                        <div class="price">
                            <p class="price fw-bolder text-info fs-lg-4 fs-xl-4 m-0">{{ formattedFinalPrice }}</p>
                        </div>
                    </div>
                    <div :class="finalPrice > 0 ? 'col-7 p-0' : 'col-12 p-0'">
                        <div class="d-grid gap-2">
                            <button class="btn btn-primary with-box-shadow fw-bold fs-6 mb-1"
                                    v-open-configurateur="{ ...openOptions }"
                                    :disabled="finalPrice === 0 || !voiture_id || !circuit_id">
                                {{ data.reserveLabel }}
                            </button>
                            <p class="small text-gray-50 text-end m-0">{{ data.orLabel }} <a
                                class="text-white text-decoration-none"
                                :href="`tel:${data.phoneNumber}`">{{ data.phoneNumber }}</a></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
.search-reserv .dropdown-menu {
    transform: translate3d(0px, 130px, 0px) !important;
}

#search .bootstrap-select {
    overflow-x: hidden !important;
}

/* Alignement des blocs de sélection avec le formulaire principal */
.form.search-reserv .card-step.circuits-selection,
.form.search-reserv .card-step.cars-selection {
    width: 100%;
    max-width: none;
    margin-left: 0;
    margin-right: 0;
}

/* Assurer que les blocs de sélection occupent toute la largeur disponible */
.form.search-reserv .card-step.circuits-selection .row,
.form.search-reserv .card-step.cars-selection .row {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
}
</style>
